/**
 * Spoken save confirmation for the Voice DAR workflow.
 *
 * Additive only: this is called *after* an existing save has already succeeded
 * and never gates, delays or fails the save itself. Speech synthesis runs on
 * the existing Deepgram server function (`speakText`), so the Deepgram
 * credentials stay server-side.
 */

import { speakText } from "@/lib/voice-chat.functions";

export const DAR_SAVED_SYNCED = "DAR saved successfully.";
export const DAR_SAVED_QUEUED = "DAR saved locally and queued for processing.";

/** Keys already spoken, so a re-render or retry cannot double-announce. */
const spoken = new Set<string>();
let playing: HTMLAudioElement | null = null;

/**
 * Speak a save confirmation once per `key`. Fire-and-forget: any failure
 * (offline, TTS unconfigured, autoplay blocked) is swallowed.
 */
export function speakDarSaved(text: string, key: string): void {
  if (typeof window === "undefined") return;
  const dedupeKey = `${key}:${text}`;
  if (spoken.has(dedupeKey)) return;
  spoken.add(dedupeKey);

  void (async () => {
    try {
      // Offline: the audio call would fail anyway, and the on-screen toast
      // already covers the queued case.
      if (navigator.onLine === false) return;
      const res = (await speakText({ data: { text, voice: "thalia" } })) as
        | { ok: true; mimeType: string; audioBase64: string }
        | { ok: false; error: string };
      if (!res.ok) return;
      const audio = new Audio(`data:${res.mimeType};base64,${res.audioBase64}`);
      playing?.pause();
      playing = audio;
      await audio.play();
    } catch {
      /* speech is a courtesy — never surface or block on it */
    }
  })();
}
